/**
 * Declarative source of the "Autres actions" launcher list.
 *
 * CLAUDE.md "configurable-ready" rule: this is a set of rows a tenant may want
 * to rename / hide / reorder, so it MUST come from a declarative config with
 * **stable keys** — never hardcoded JSX labels. Each `key` is the permanent
 * contract a rule targets; `label` is display-only and overridable. `order`
 * reflects the mockup order; `visible` lets a tenant drop an action without a
 * code change.
 *
 * Each descriptor also carries the backing card's **`componentId`** (the card's
 * existing registered id, e.g. `'claimflow.emailCard'`) so the launcher can
 * resolve a stable DOM anchor — the card self-anchors with {@link actionAnchorId}
 * on its own root — without the cards ever knowing the launcher's keys.
 *
 * The `key` ↔ backing-card mapping (and each card's "present" render guard)
 * lives in `pages/TaskDetailPage.tsx` (`renderInjected`) and in
 * `.claude-work/specs/other-actions-launcher.md` (the action table).
 */

/**
 * A single launcher entry. `key` is the stable contract key; `label` is the
 * mockup wording (display-only); `componentId` is the backing card's registered
 * id (drives the DOM anchor); `order` is the ascending sort position; `visible`
 * gates the row.
 */
export interface OtherActionDescriptor {
  /** Stable contract key — the permanent identifier a rule targets. */
  key: string;
  /** Display-only label (mockup wording). Overridable via layout rules. */
  label: string;
  /** The backing card's registered id, e.g. `'claimflow.emailCard'`. */
  componentId: string;
  /** Ascending sort position (lower = first). */
  order: number;
  /** Whether the row is shown at all. */
  visible: boolean;
}

/**
 * The launcher rows, in mockup order. `CommentsPanel` ("Laisser un
 * commentaire") and "Rattacher des docs reçus" are intentionally excluded (see
 * spec): the former is its own always-on panel, the latter has no component.
 *
 * Note: `label` is display-only and independent of the backing card's own
 * title. The `key` stays stable even when the label changes (e.g.
 * `nouvelleProvision` is labelled "Nouvelle estimation").
 */
export const OTHER_ACTIONS: readonly OtherActionDescriptor[] = [
  { key: 'ouvertureSinistre', label: 'Ouverture sinistre', componentId: 'claimflow.ouvertureSinistreCard', order: 0, visible: true },
  { key: 'nouvelleProvision', label: 'Nouvelle estimation', componentId: 'claimflow.estimationCard', order: 1, visible: true },
  { key: 'ajouterIntervenant', label: 'Ajouter un intervenant', componentId: 'claimflow.intervenantForm', order: 2, visible: true },
  { key: 'modifierIntervenant', label: 'Modifier un intervenant', componentId: 'claimflow.editIntervenantCard', order: 3, visible: true },
  { key: 'ouvertureGaranties', label: 'Ouverture de garanties', componentId: 'claimflow.openCoverageCard', order: 4, visible: true },
  { key: 'clotureGarantie', label: 'Clôture garantie', componentId: 'claimflow.closeCoverageCard', order: 5, visible: true },
  { key: 'email', label: 'Email', componentId: 'claimflow.emailCard', order: 6, visible: true },
  { key: 'miseAJourDossier', label: 'Mise à jour dossier', componentId: 'claimflow.miseAJourDossierCard', order: 7, visible: true },
  { key: 'clotureSinistre', label: 'Clôture sinistre', componentId: 'claimflow.clotureSinistreCard', order: 8, visible: true },
  { key: 'creerTache', label: 'Créer une tâche', componentId: 'claimflow.createTaskCard', order: 9, visible: true },
  { key: 'editerDoc', label: 'Éditer un doc', componentId: 'claimflow.editDocumentCard', order: 10, visible: true },
  { key: 'reglement', label: 'Règlement', componentId: 'claimflow.settlementCard', order: 11, visible: true },
  { key: 'encaisserRecours', label: 'Encaisser un recours', componentId: 'claimflow.recoveryCard', order: 12, visible: true },
  { key: 'piecesMqDemandees', label: 'Pièces MQ demandées', componentId: 'claimflow.requestPiecesCard', order: 13, visible: true },
  { key: 'piecesMqRecues', label: 'Pièces MQ reçues', componentId: 'claimflow.missingPiecesCard', order: 14, visible: true },
  { key: 'estimationRecours', label: 'Estimation recours', componentId: 'claimflow.estimationRecoursCard', order: 15, visible: true },
];

/**
 * Single source of truth for a card's launcher DOM anchor id. The backing card
 * stamps this on its root element (`Card`'s `<article>` forwards `id`); the
 * launcher hook resolves it with `document.getElementById(actionAnchorId(...))`
 * to scroll/highlight an already-mounted card (or a freshly injected one). Dots
 * in the component id are swapped for dashes to keep the id CSS/selector-safe.
 */
export const actionAnchorId = (componentId: string): string =>
  `action-anchor-${componentId.replace(/\./g, '-')}`;
